"use client";

import React, { useEffect, useState } from "react";

import Link from "next/link";
import Image from "@/helpers/Image";
import { cn } from "@/lib/utils";
import { resolveLink, resolvePageLink } from "@/sanity/globals/CTA";
import { IFooter } from "@/sanity/lib/definitions";
import { PortableText } from "next-sanity";
import { richTextComponent } from "@/sanity/sanityPortableText";
import { DEFAULT_LANGUAGE } from "@/sanity/globals/I18n";

import logo from "@/public/logo-black.svg";
import waveDots from "@/public/fabio-affiliate/noise.jpg";

export default function FooterFabio({
  data,
  className,
  slug,
  alternativeRiskDisclosure = false,
  isBeyondBoring = false,
  language,
}: {
  data: IFooter;
  className?: string;
  slug?: string;
  alternativeRiskDisclosure?: boolean;
  isBeyondBoring?: boolean;
  language?: string;
}) {
  const isBrazil = slug?.endsWith("/br");

  const [loaded, setLoaded] = useState(false);

  const homeHref = resolveLink(
    language && language !== DEFAULT_LANGUAGE ? `/${language}` : "/",
    language || DEFAULT_LANGUAGE,
  );

  const [finalHomeLink, setFinalHomeLink] = useState("");

  useEffect(() => {
    if (loaded) return;
    setLoaded(true);

    return () => {
      setLoaded(false);
    };
  }, []);

  useEffect(() => {
    if (!homeHref) return;
    setFinalHomeLink(homeHref);
  }, [homeHref]);

  // Every hook above runs on every render: this early return must stay below
  // them, otherwise the hook count changes once `loaded` flips to true.
  if (!loaded) return null; // Prevent rendering on server side to avoid hydration mismatch

  return (
    <footer
      className={cn(
        "w-full px-[2rem] lg:px-[5rem] py-[3rem] lg:py-[4rem] flex flex-col gap-[4rem] lg:gap-[2rem] items-center relative bg-[#F4F4F4]",
        className,
      )}
    >
      <Image
        src={waveDots}
        alt=""
        className="absolute inset-0 object-cover !w-full !h-full mix-blend-exclusion opacity-10 pointer-events-none"
      />
      <Link className="hidden lg:flex  w-fit" href={finalHomeLink}>
        <Image alt="Logo" src={logo} className="w-[16.375rem]" />
      </Link>
      <div className="flex flex-col lg:flex-row items-center lg:justify-between gap-[1.5rem] w-[min(140rem,100%)] mx-auto ">
        <Link className="lg:hidden" href={finalHomeLink}>
          <Image alt="Logo" src={logo} className="w-[16.375rem]" />
        </Link>
        <div className="flex flex-col lg:flex-row items-center gap-[2.5rem]">
          <p className="mb-0 text-[1.75rem] text-[#141719]/8">
            © {new Date().getFullYear()}{" "}
            {isBeyondBoring ? "Beyond IQ Capital" : "IQ Capital"} -{" "}
            {isBrazil ? "Todos os direitos reservados" : "All rights reserved"}
          </p>
        </div>

        {data?.navColumns?.length > 0 &&
          (() => {
            const legalLinks =
              data.navColumns[data.navColumns.length - 1].links;
            return (
              <div className="flex flex-wrap justify-center items-center gap-[1.5rem]">
                {legalLinks.map((item, index) => (
                  <React.Fragment key={index}>
                    <Link
                      className="opacity-80 font-geist transition-opacity hover:opacity-100 text-[#454748] text-[1.75rem]"
                      href={resolvePageLink(item.link, language) ?? "/"}
                      target={item.link?.openInNewTab ? "_blank" : "_self"}
                    >
                      {item.title}
                    </Link>
                    {index < legalLinks.length - 1 && (
                      <span className="opacity-80 text-[#454748] text-[1.75rem]">
                        /
                      </span>
                    )}
                  </React.Fragment>
                ))}
              </div>
            );
          })()}
      </div>
      {data?.disclaimer && (
        <div
          className={cn(
            "richtext richtext-start riskdisclosure  prose-p:text-[#454748]/80 prose-p:text-[1.75rem] prose-p:text-center [text-wrap:balance] lg:prose-p:max-w-[97.5rem] w-fit",
            alternativeRiskDisclosure
              ? "bg-[#F9F9F9] rounded-[2rem] lg:rounded-[5.25rem] px-[2rem] py-[4rem] lg:py-[5rem]  richtext-center lg:[text-wrap-style:balance] prose-p:text-[#454748]/60 lg:prose-p:max-w-[97.5rem] !prose-p:text-center"
              : "",
          )}
        >
          <PortableText
            value={data?.disclaimer}
            components={richTextComponent}
          />
        </div>
      )}
    </footer>
  );
}
